import React from "react";

import PageHead from "../../components/PageHead";
import * as api from "./api";
import { Async, Panel } from "./panels";
import { useEndpoint } from "./useOffer";

/**
 * Workstation · Learnings (§4, tab 7 of 8).
 *
 * `design/system/audits/workstation-tabs.md` §7
 * ==============================================
 * Backed, but NOT offer-scoped and largely founder-gated — this tab is
 * honest about both rather than implying a scope the backend does not have.
 *
 *   GET /api/intelligence/experiments    performance.py:286  require_founder
 *   GET /api/intelligence/observations   performance.py:292  require_founder
 *   GET /api/intelligence/hypotheses     performance.py:298  require_founder
 *
 * All three run through the shared `_intelligence_list` helper
 * (`performance.py:274-283`), which reads each `cipher_*` collection with a
 * literal empty query `{}` across BOTH business-unit databases and returns
 * everything — no `offer_id` parameter, no pagination. So this tab does NOT
 * take the selected offer as a filter (unlike every other Workstation tab);
 * it reads and renders the account-wide feed as what it is.
 *
 * `GET /creative-intelligence/top-patterns` and `/fatigue-benchmarks`
 * (`predictions.py:493`, `:505`) are the same family but NOT founder-gated —
 * `get_current_operator` only — so they render for every signed-in operator
 * even when the three panels above show the founder-only refusal.
 *
 * WHAT THIS TAB DOES NOT WIRE, AND WHY
 * =====================================
 * `/api/lessons`, `/api/journal`, `/api/outcomes` are all flag-gated 404 when
 * off (`INSTITUTIONAL_LEARNING_ENABLED`, `EXECUTIVE_JOURNAL_ENABLED`,
 * `OUTCOME_SPINE_ENABLED` — all default `false`) and none of them are
 * offer-filterable either. WORKSTATION-OPPORTUNITIES-APPROVED already ruled
 * `/api/lessons` out of the Opportunities tab for exactly this reason,
 * scoped narrowly to that tab; the same reasoning applies here. Wiring three
 * more account-wide, possibly-404ing families onto an already
 * not-offer-scoped tab does not make the tab more useful — the two
 * unflagged, unambiguously-live families above (`/intelligence/*` and
 * `/creative-intelligence/*`) are what ship.
 */

function ExperimentRow({ item }) {
  return (
    <li className="ws-understanding-item">
      <span>{item.name || item.experiment_id || item.title}</span>
      <span className="ws-tag">{item.status || "—"}</span>
    </li>
  );
}

function ObservationRow({ item }) {
  return (
    <li className="ws-understanding-item">
      <span>{item.summary || item.observation || item.text || item.observation_id}</span>
      <span className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
        {item.created_at ? String(item.created_at).slice(0, 10) : ""}
      </span>
    </li>
  );
}

function HypothesisRow({ item }) {
  return (
    <li className="ws-understanding-item">
      <span>{item.hypothesis_text || item.hypothesis_id}</span>
      <span className="ws-tag">{item.status || "—"}</span>
    </li>
  );
}

function IntelligenceList({ title, subtitle, result, RowComponent, listKey, emptyDetail }) {
  return (
    <Panel title={title} subtitle={subtitle}>
      <Async
        result={result}
        empty={emptyDetail}
        deniedLabel="Learnings' experiment/observation/hypothesis feed is founder-only, and the server refused it. That is the gate working, not a fault."
      >
        {(data) => {
          const rows = data?.[listKey] || [];
          if (rows.length === 0) {
            return <div className="state ws-state">{emptyDetail}</div>;
          }
          return (
            <ul className="ws-list">
              {rows.slice(0, 20).map((r, i) => (
                <RowComponent key={r._id || i} item={r} />
              ))}
            </ul>
          );
        }}
      </Async>
    </Panel>
  );
}

function TopPatterns() {
  const patterns = useEndpoint(() => api.getTopPatterns(), []);
  return (
    <Panel
      title="Top-performing patterns"
      subtitle="Ranked by CPA. Not founder-gated — visible to any signed-in operator."
    >
      <Async result={patterns} empty="No creative patterns recorded yet.">
        {(data) => {
          const rows = Array.isArray(data) ? data : data?.patterns || [];
          if (rows.length === 0) {
            return <div className="state ws-state">No creative patterns recorded yet.</div>;
          }
          return (
            <ul className="ws-list">
              {rows.slice(0, 15).map((p, i) => (
                <li key={p.intelligence_id || i} className="ws-understanding-item">
                  <span>{p.attribute_value || p.intelligence_id}</span>
                  <span className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
                    {p.avg_cpa !== undefined ? `CPA $${Number(p.avg_cpa).toFixed(2)}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          );
        }}
      </Async>
    </Panel>
  );
}

function FatigueBenchmarks() {
  const fatigue = useEndpoint(() => api.getFatigueBenchmarks(), []);
  return (
    <Panel title="Fatigue benchmarks" subtitle="Per pattern. Not founder-gated.">
      <Async result={fatigue} empty="No fatigue benchmarks recorded yet.">
        {(data) => {
          const rows = Array.isArray(data) ? data : data?.benchmarks || [];
          if (rows.length === 0) {
            return <div className="state ws-state">No fatigue benchmarks recorded yet.</div>;
          }
          return (
            <ul className="ws-list">
              {rows.slice(0, 15).map((b, i) => (
                <li key={b.intelligence_id || i} className="ws-understanding-item">
                  <span>{b.attribute_value || b.intelligence_id}</span>
                  <span className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
                    {b.avg_fatigue_days !== undefined ? `${b.avg_fatigue_days} avg fatigue days` : ""}
                  </span>
                </li>
              ))}
            </ul>
          );
        }}
      </Async>
    </Panel>
  );
}

export default function Learnings() {
  const experiments = useEndpoint(() => api.getIntelligenceExperiments(), []);
  const observations = useEndpoint(() => api.getIntelligenceObservations(), []);
  const hypotheses = useEndpoint(() => api.getIntelligenceHypotheses(), []);

  return (
    <>
      <PageHead
        title="Learnings"
        subtitle="Accumulated institutional learning across the account — not filtered to one offer."
      />

      <div className="state ws-state" role="status" style={{ marginBottom: "var(--sp-5, 20px)" }}>
        <p className="dim" style={{ margin: 0 }}>
          Unlike every other Workstation tab, this one is not offer-scoped. The endpoints behind
          it (<code>/api/intelligence/experiments</code>, <code>/observations</code>,{" "}
          <code>/hypotheses</code>) read across both business units with no <code>offer_id</code>{" "}
          filter and no pagination — the account-wide feed is what they have, so the account-wide
          feed is what renders here. The three panels below are also founder-only at the server;
          a non-founder operator sees the designed refusal, not a blank tab.
        </p>
      </div>

      <div className="ws-analytics-grid">
        <IntelligenceList
          title="Experiments"
          subtitle="`cipher_experiments`, both business units. Founder-only."
          result={experiments}
          RowComponent={ExperimentRow}
          listKey="experiments"
          emptyDetail="No experiments recorded yet."
        />
        <IntelligenceList
          title="Observations"
          subtitle="`cipher_observations`, both business units. Founder-only."
          result={observations}
          RowComponent={ObservationRow}
          listKey="observations"
          emptyDetail="No observations recorded yet."
        />
        <IntelligenceList
          title="Hypotheses"
          subtitle="`cipher_hypotheses`, both business units. Founder-only."
          result={hypotheses}
          RowComponent={HypothesisRow}
          listKey="hypotheses"
          emptyDetail="No hypotheses recorded yet."
        />
        <TopPatterns />
        <FatigueBenchmarks />
      </div>
    </>
  );
}
